// One feed per selection. Consumers that share a selection share its rows,
// its error status and its subscription; a different selection is a different
// feed because the server applies it before the row limit.
const states = new Map();
function getState(filter) {
    let state = states.get(filter);
    if (!state) {
        state = { listeners: new Set(), snapshot: null, hasError: false };
        states.set(filter, state);
    }
    return state;
}
export function hasSidebarRecentItemsListeners(filter) {
    return getState(filter).listeners.size > 0;
}
export function hasSidebarRecentItemsSnapshot(filter) {
    return getState(filter).snapshot !== null;
}
export function hasSidebarRecentItemsError(filter) {
    return getState(filter).hasError;
}
export function getSidebarRecentItemsSnapshot(filter) {
    return getState(filter).snapshot;
}
export function publishSidebarRecentItemsSnapshot(items, { filter, preserveError = false }) {
    const state = getState(filter);
    state.snapshot = items;
    // Optimistic local updates pass preserveError so they cannot masquerade
    // as transport recovery: only an authoritative feed (initial query,
    // subscription snapshot) clears the error. A preserved error is re-fanned
    // after the rows so consumers paint the change but keep their fallback.
    const keepError = preserveError && state.hasError;
    state.hasError = keepError;
    for (const listener of state.listeners)
        listener.onSnapshot(items);
    if (!keepError)
        return;
    for (const listener of state.listeners)
        listener.onError();
}
// Apply optimistic updates to every retained snapshot, including any previous
// selection still being released during a filter switch.
function optimisticallySetSidebarItemUnread(contextUUID, unread) {
    for (const [filter, state] of states) {
        if (state.snapshot === null)
            continue;
        let changed = false;
        const next = state.snapshot.map((item) => {
            if (item.context_uuid !== contextUUID || item.unread === unread)
                return item;
            changed = true;
            return { ...item, unread };
        });
        if (changed)
            publishSidebarRecentItemsSnapshot(next, { filter, preserveError: true });
    }
}
export function optimisticallyMarkSidebarItemRead(contextUUID) {
    optimisticallySetSidebarItemUnread(contextUUID, false);
}
export function optimisticallyMarkSidebarItemUnread(contextUUID) {
    optimisticallySetSidebarItemUnread(contextUUID, true);
}
export function publishSidebarRecentItemsError(filter) {
    const state = getState(filter);
    // The snapshot is kept: a transport failure with rows already loaded must
    // degrade to a static list while the retained subscription reopens with
    // backoff, not discard what the user is looking at. Listeners still hear
    // the error so source selection can fall back to REST.
    if (state.hasError)
        return;
    state.hasError = true;
    for (const listener of state.listeners)
        listener.onError();
}
export function addSidebarRecentItemsListener(listener, filter) {
    const state = getState(filter);
    const isFirst = state.listeners.size === 0;
    state.listeners.add(listener);
    if (state.snapshot !== null)
        listener.onSnapshot(state.snapshot);
    // Replayed after the snapshot so a listener mounting mid-outage converges
    // with live listeners: last rows applied, then the error status.
    if (state.hasError)
        listener.onError();
    return {
        isFirst,
        release: () => {
            state.listeners.delete(listener);
            return state.listeners.size === 0;
        },
    };
}
export function resetSidebarRecentItemsState(filter) {
    const state = getState(filter);
    state.snapshot = null;
    state.hasError = false;
}
export function updateRecentThreadCollection(sessions, { collectionUuid, contextUUID, entryUUID }) {
    const matchesThread = (session) => session.sidebarItemType !== 'automation' &&
        (session.uuid === entryUUID || session.context_uuid === contextUUID);
    return {
        items: sessions.map((session) => matchesThread(session) ? { ...session, collection_uuid: collectionUuid } : session),
        snapshot: {
            optimisticCollectionUuid: collectionUuid,
            threads: sessions.filter(matchesThread).map(({ collection_uuid, context_uuid, uuid }) => ({
                collection_uuid,
                context_uuid,
                uuid,
            })),
        },
    };
}
export function restoreRecentThreadCollection(sessions, snapshot) {
    return sessions.map((session) => {
        if (session.sidebarItemType === 'automation')
            return session;
        const previous = snapshot.threads.find(({ context_uuid, uuid }) => uuid === session.uuid && context_uuid === session.context_uuid);
        return previous && session.collection_uuid === snapshot.optimisticCollectionUuid
            ? { ...session, collection_uuid: previous.collection_uuid }
            : session;
    });
}
export function updateSidebarRecentThreadCollection(update) {
    const snapshots = new Map();
    if (!update.contextUUID && !update.entryUUID)
        return snapshots;
    for (const [filter, state] of states) {
        if (state.snapshot === null)
            continue;
        const result = updateRecentThreadCollection(state.snapshot, update);
        publishSidebarRecentItemsSnapshot(result.items, { filter, preserveError: true });
        snapshots.set(filter, result.snapshot);
    }
    return snapshots;
}
export function restoreSidebarRecentThreadCollection(snapshots) {
    for (const [filter, snapshot] of snapshots) {
        const items = getSidebarRecentItemsSnapshot(filter);
        if (items === null)
            continue;
        publishSidebarRecentItemsSnapshot(restoreRecentThreadCollection(items, snapshot), {
            filter,
            preserveError: true,
        });
    }
}
//# sourceMappingURL=sidebarRecentItemsState.js.map